import { useState, type ReactNode } from 'react';

import {
  persistLocalQuizOnLogin,
  resolveFreemiumPostLoginRoute,
} from '@hungryroot/funnel';
import { usePromoData } from '@hungryroot/promos';
import { type SignupResponse } from '@hungryroot/rest';
import { ButtonApple, ButtonFacebook, ButtonGoogle } from '@hungryroot/sso';
import { Header, Heading, Paragraph, Text } from '@hungryroot/ui-kit';
import { AlreadyHaveAnAccount } from '../AlreadyHaveAnAccount/AlreadyHaveAnAccount';
import { Faq } from '../Faq/Faq';
import { SignUpForm } from '../SignUpForm/SignUpForm';

import styles from './SignUp.module.css';

type Props = {
  header: ReactNode;
  onNavigate: (route: string) => void;
  nonCheckedOutSignupSuccessUrl: string;
  onSignupSuccess: (data: {
    provider: 'email' | 'facebook' | 'apple' | 'google';
    data: SignupResponse;
  }) => void;
};

export function SignUp({
  header,
  onNavigate,
  onSignupSuccess,
  nonCheckedOutSignupSuccessUrl,
}: Props) {
  const isRedeemingGiftCard = nonCheckedOutSignupSuccessUrl.includes('redeem'); // ?next=/gift/redeem/:code

  /**
   * An existing account signing in through an SSO button on the signup
   * screen is a login, so it gets the same local-quiz hand-off as `/login/`
   * before leaving the page, and a freemium-treatment guest then lands on
   * the feed or the explicit-selections deck instead of the quiz tail.
   * Both helpers never throw and are bounded, but the redirect is in
   * `finally` regardless.
   */
  async function persistQuizThenNavigate(user: SignupResponse) {
    let route =
      user.did_checkout === true
        ? '/myhungryroot/'
        : nonCheckedOutSignupSuccessUrl;
    try {
      const quizPersistOutcome = await persistLocalQuizOnLogin({
        customer: user,
      });
      route =
        (await resolveFreemiumPostLoginRoute({
          customer: user,
          quizPersistOutcome,
        })) ?? route;
    } finally {
      onNavigate(route);
    }
  }

  function onSsoLoginSuccess(user: SignupResponse) {
    // ?next=/gift/redeem/:code as an example. The quiz is deferred until
    // after redemption, so there is nothing to hand off here.
    if (isRedeemingGiftCard) {
      window.location.href = nonCheckedOutSignupSuccessUrl;
      return;
    }

    void persistQuizThenNavigate(user);
  }

  const [ssoError, setSsoError] = useState(false);

  const { data: promoData } = usePromoData({
    page: '/signup/',
  });

  const title = isRedeemingGiftCard
    ? 'Sign up to redeem your gift card'
    : 'Unlock your personalized recommendations';
  let description: string;
  switch (true) {
    case isRedeemingGiftCard:
      description = 'Create an account below to receive your feel good foods.';
      break;
    case !!promoData?.discount_effect:
      description = `Create an account to claim your free gift for life and ${promoData?.discount_effect}.`;
      break;
    default:
      description = 'Sign up now for 30% off and a free gift for life.';
  }

  const alreadyHaveAnAccount = (
    <AlreadyHaveAnAccount
      nextRedirect={
        isRedeemingGiftCard ? nonCheckedOutSignupSuccessUrl : undefined
      }
    />
  );

  return (
    <div>
      {isRedeemingGiftCard ? <Header /> : header}
      <main className={styles.main}>
        <div className={styles.header}>
          <Heading className={styles.title} variant="H1Alt">
            {title}
          </Heading>
          {description ? (
            <Paragraph className={styles.promo}>{description}</Paragraph>
          ) : null}
        </div>
        <SignUpForm
          submitButtonText={
            isRedeemingGiftCard ? 'Continue' : 'Choose free gift'
          }
          heading={null}
          onSignupSuccess={(data) => {
            onSignupSuccess({
              data,
              provider: 'email',
            });
          }}
          ssoButtons={
            <>
              <div className={styles.sso}>
                <ButtonGoogle
                  onError={() => setSsoError(true)}
                  onSignupSuccess={(data) => {
                    onSignupSuccess({
                      data,
                      provider: 'google',
                    });
                  }}
                  onLoginSuccess={onSsoLoginSuccess}
                >
                  <Text weight="heavy" className={styles['sso-copy']}>
                    Sign in with Google
                  </Text>
                </ButtonGoogle>
                <ButtonApple
                  onError={() => setSsoError(true)}
                  onSignupSuccess={(data) => {
                    onSignupSuccess({
                      data,
                      provider: 'apple',
                    });
                  }}
                  onLoginSuccess={onSsoLoginSuccess}
                >
                  <Text weight="heavy" className={styles['sso-copy']}>
                    Sign in with Apple
                  </Text>
                </ButtonApple>
                <ButtonFacebook
                  onError={() => setSsoError(true)}
                  onSignupSuccess={(data) => {
                    onSignupSuccess({
                      data,
                      provider: 'facebook',
                    });
                  }}
                  onLoginSuccess={onSsoLoginSuccess}
                >
                  <Text weight="heavy" className={styles['sso-copy-facebook']}>
                    Continue with Facebook
                  </Text>
                </ButtonFacebook>
              </div>
              {ssoError && (
                <Paragraph className={styles['error-sso']} variant="caption">
                  The account cannot be accessed because the login credentials
                  could not be verified. Please try again or use a different
                  account to sign up.
                </Paragraph>
              )}
            </>
          }
        >
          <div className={styles['already-have-an-account']}>
            {alreadyHaveAnAccount}
          </div>
        </SignUpForm>
        <Faq />
      </main>
    </div>
  );
}
